'use client';

import React from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { ChevronRight, ChevronLeft } from 'lucide-react';

interface BestArchiveClientProps {
  categories: string[];
  selectedCategory: string;
  currentPage: number;
  totalPages: number;
  showPagination?: boolean;
}

// Client component for category filters and pagination interactivity
const BestArchiveClient: React.FC<BestArchiveClientProps> = ({
  categories,
  selectedCategory,
  currentPage,
  totalPages,
  showPagination = false,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Handle category selection
  const handleCategoryChange = (category: string) => {
    if (category === selectedCategory) return;
    
    const newParams = new URLSearchParams();
    newParams.set('page', '1');
    if (category !== 'All') {
      newParams.set('category', category);
    }
    router.push(`/best?${newParams.toString()}`);
  };

  // Handle page navigation
  const handlePageChange = (pageNumber: number) => {
    if (pageNumber === currentPage) return;
    
    const validPageNumber = Math.min(Math.max(1, pageNumber), totalPages);
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set('page', validPageNumber.toString());
    router.push(`/best?${newParams.toString()}`);
  };

  // Render only pagination if showPagination is true
  if (showPagination) {
    return (
      <div className="flex justify-center mt-8 mb-6">
        <nav className="w-full max-w-2xl" aria-label="Pagination">
          {/* Page numbers row */}
          <div className="flex flex-wrap items-center justify-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                  currentPage === pageNum
                    ? 'bg-pink-500 text-charcoal-950 font-bold'
                    : 'bg-charcoal-900/50 text-pink-200/70 hover:bg-charcoal-800/50 cursor-pointer'
                }`}
                aria-label={`Go to page ${pageNum}`}
                aria-current={currentPage === pageNum ? 'page' : undefined}
              >
                {pageNum}
              </button>
            ))}
          </div>

          {/* Controls row */}
          <div className="flex items-center justify-center gap-3 mt-4">
            <button
              onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
              className={`px-4 py-2 rounded-lg flex items-center gap-1 ${
                currentPage === 1 
                  ? 'bg-charcoal-900/30 text-pink-200/30 cursor-not-allowed' 
                  : 'bg-charcoal-900/50 text-pink-200/70 hover:bg-charcoal-800/50 hover:text-pink-400 cursor-pointer'
              }`}
              disabled={currentPage === 1}
              aria-label="Go to previous page"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>

            <button
              onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
              className={`px-4 py-2 rounded-lg flex items-center gap-1 ${
                currentPage === totalPages 
                  ? 'bg-charcoal-900/30 text-pink-200/30 cursor-not-allowed' 
                  : 'bg-charcoal-900/50 text-pink-200/70 hover:bg-charcoal-800/50 hover:text-pink-400 cursor-pointer'
              }`}
              disabled={currentPage === totalPages}
              aria-label="Go to next page"
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </nav>
      </div>
    );
  }

  // Render category filters
  return (
    <div className="flex items-center justify-center flex-wrap gap-3 mb-8">
      {categories.map((category, index) => (
        <div key={index} className="relative">
          <button
            onClick={() => handleCategoryChange(category)}
            className={`btn-outline rounded-full px-4 py-2 text-sm font-semibold transition-all ${
              selectedCategory === category
                ? 'bg-pink-400/10 border-pink-400/50 text-pink-300'
                : ''
            }`}
          >
            {category}
          </button>
        </div>
      ))}
    </div>
  );
};

export default BestArchiveClient;

